/**
 * Retry policy for the app-layout auth gate's /api/auth/me query (FRONT-340).
 *
 * The gate must distinguish two very different failures:
 * - 401 → a definitive "not signed in". Stop retrying; the gate converts it
 *   to `{ authenticated: false }` and redirects to login.
 * - anything else (429 rate-limited, 5xx, network) → UNKNOWN. The session may
 *   be perfectly fine — e.g. a zombie tab elsewhere on the same IP exhausted
 *   the rate-limit bucket. Bouncing to login here is what produced the
 *   all-night login loop. Instead: retry forever with exponential backoff,
 *   capped, until we get a definitive answer.
 */

export function authMeRetry(_failureCount: number, error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status !== 401;
}

/** 1s → 2s → 4s → … capped at 30s between attempts. */
export function authMeRetryDelay(attempt: number): number {
  return Math.min(1_000 * 2 ** attempt, 30_000);
}

/**
 * FRONT-561: collapse the gate's inputs into render state.
 *
 * A confirmed dead session — `sessionLost`, set when the api-client fires
 * SESSION_LOST_EVENT after a 401 whose silent refresh also failed — forces
 * logged-out AND ready, so the layout redirects to login instead of hanging on
 * "Loading your conversation…". This is the case `/api/auth/me` can't catch on
 * its own: the cookie still parses (so /me says authenticated), but the access
 * token is expired and unrefreshable, so every protected call 401s. Without
 * this, the session-resolve gate never settles and the loader spins forever.
 *
 * Otherwise the /me answer governs: ready once the query settled, authed per
 * its result.
 *
 * FRONT-2096: `eddyAccess` is the Eddy SEAT, separate from `authed`. A tenant
 * member without a seat IS signed in (they can open what colleagues share),
 * but the shells render the "you need Eddy access" screen instead of the app.
 * Only an explicit `false` from /me is a no-seat answer — a server that does
 * not send the field yet, or a /me still in flight, reads as seated, so an
 * older backend never shows the screen to everyone.
 */
export function authGateView(input: {
  meAuthenticated: boolean | undefined;
  meEddyAccess?: boolean | undefined;
  settled: boolean;
  sessionLost: boolean;
}): { ready: boolean; authed: boolean; eddyAccess: boolean } {
  const eddyAccess = input.meEddyAccess !== false;
  if (input.sessionLost) return { ready: true, authed: false, eddyAccess };
  return { ready: input.settled, authed: input.meAuthenticated ?? false, eddyAccess };
}
